<script>
import moment from 'moment'
import LongStayPrice from '../TabSpecialPrice/LongStayPrice.vue'
import { postHotelRoomAvailability } from '../api'

export default {
  components: {
    LongStayPrice,
  },
  props: {
    // 父層 index.vue 抓到的 /admin/merchant/setting { type:'hotel' } 回傳 data
    hotelInfo: {
      type: Object,
      default: () => null,
    },
    // 父層抓到的 /admin/product/search 回傳（你說 productsInfo.items）
    productsInfo: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      activeSubTab: 'room-control',
      currentBranch: null,
      branches: [],
      date: new Date().toISOString().slice(0, 10),
      activeRooms: [1],
      rooms: [],
      isLoading: false,
    }
  },
  computed: {
    currentBranchName() {
      const branch = this.branches.find((b) => b.id === this.currentBranch)
      this.$store.commit('hotel/setting/SET_PRODUCT_NAME', branch.name)
      return branch ? branch.name : ''
    },
    mondayDate() {
      return moment(this.date).startOf('isoWeek')
    },
    days() {
      const days = Array.from({ length: 7 }).map((_, i) =>
        moment(this.mondayDate)
          .add(i, 'days')
          .format('YYYY-MM-DD')
      )
      return days
    },
  },
  async activated() {
    // 每次「進入」或「返回」這個組件時都會觸發
    await this.initHotelRoomAvailability()
  },
  async created() {
    this.initHotelRoomAvailability()
  },
  methods: {
    activeRoom(room) {
      return this.activeRooms.includes(room.id)
    },
    dayPrice(day, price) {
      return day.available ? this.formatPrice(price) : '不開放'
    },
    formatPrice(price) {
      return `NT ${price.toLocaleString('zh-TW', { style: 'currency', currency: 'TWD', minimumFractionDigits: 0 })}`
    },
    formatDate(date) {
      return moment(date).format('DD')
    },
    formatWeekday(weekday) {
      return ['日', '一', '二', '三', '四', '五', '六', '日'][moment(weekday).day()]
    },
    formatMonthYear(date) {
      return moment(date).format('YYYY年MM月')
    },
    prevWeek() {
      this.date = moment(this.date)
        .subtract(1, 'week')
        .format('YYYY-MM-DD')
      this.initHotelRoomAvailability()
    },
    nextWeek() {
      this.date = moment(this.date)
        .add(1, 'week')
        .format('YYYY-MM-DD')
      this.initHotelRoomAvailability()
    },
    isToday(date) {
      return moment(date).isSame(moment(), 'day')
    },
    toToday() {
      this.date = moment().format('YYYY-MM-DD')
      this.initHotelRoomAvailability()
    },
    toggleRoom(room) {
      if (this.activeRooms.includes(room.id)) {
        this.activeRooms = this.activeRooms.filter((id) => id !== room.id)
      } else {
        this.activeRooms.push(room.id)
      }
    },
    async fetchHotelRoomAvailability(payload) {
      try {
        this.isLoading = true
        this.$message.info('取得最新資料中，請稍候...')
        const res = await postHotelRoomAvailability(payload)
        this.rooms = res.items
        this.$message.success('已更新當前房型及房況資料')
      } catch (error) {
        console.error('獲取現有房型及房況失敗:', error)
      } finally {
        this.isLoading = false
      }
    },
    async onClickChangeBranch(productId) {
      this.currentBranch = productId
      this.$store.commit('hotel/setting/SET_PRODUCT_ID', productId)
      await this.fetchHotelRoomAvailability({ productId, startDate: this.days[0], endDate: this.days[6] })
    },
    async initHotelRoomAvailability() {
      await this.fetchHotelRoomAvailability({ productId: this.currentBranch, startDate: this.days[0], endDate: this.days[6] })
    },
    allCategoriesText(categories) {
      if (!categories || !Array.isArray(categories)) {
        return ''
      }
      return categories.map((item) => (typeof item === 'string' ? item : item.label)).join('、\n')
    },
  },
  watch: {
    productsInfo: {
      immediate: true,
      deep: true,
      handler(val) {
        // 防呆：確保 val 與 val.items 存在且為陣列
        if (!val || !Array.isArray(val.items)) {
          this.branches = [] // 沒有資料就清空
          this.currentBranch = null // 沒有分店就設為 null
          return
        }

        // 將分店資訊更新到選單
        this.branches = val.items.map((product) => ({
          id: product.id,
          name: product.name,
        }))

        const firstId = this.branches?.[0]?.id || null
        this.currentBranch = firstId // 讓選單自動載入第一個分店
        this.$store.commit('hotel/setting/SET_PRODUCT_ID', firstId)
      },
    },
  },
}
</script>

<template>
  <div>
    <div :class="$style.header">
      <div :class="$style.branchSelector">
        <el-dropdown trigger="click" @command="onClickChangeBranch($event)" popper-class="custom-branch-dropdown" placement="bottom-start" style="width: 100%">
          <span class="el-dropdown-link" :class="$style.dropdownLink">
            {{ currentBranchName }}
            <i class="el-icon-arrow-down el-icon--right"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="branch in branches" :key="branch.id" :command="branch.id" :class="{ 'is-active': branch.id === currentBranch }">
              {{ branch.name }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div :class="$style.tabs">
        <div :class="[$style.tabItem, { [$style.active]: activeSubTab === 'room-control' }]" @click="activeSubTab = 'room-control'"> 房型列表 </div>
        <div :class="[$style.tabItem, { [$style.active]: activeSubTab === 'long-stay' }]" @click="activeSubTab = 'long-stay'"> 房價調整 </div>
      </div>
    </div>

    <div v-if="activeSubTab === 'room-control'" :class="$style.content">
      <div class="calendar-header-wrapper">
        <el-row type="flex">
          <el-col :span="6" class="header-left-box">
            <h1 class="room-list-title">房型列表</h1>
            <router-link to="/hotel?name=new-room">
              <el-button class="is-round add-room-btn" type="primary" size="small">
                <BaseIcon name="plus" />
                <span>新增房型</span>
              </el-button>
            </router-link>
          </el-col>

          <el-col :span="18" class="header-right-box">
            <div class="month-display" @click="toToday" :disabled="isLoading">
              <BaseIcon name="calendar-check" icon-style="fal" />
              {{ formatMonthYear(date) }}
            </div>
            <el-row class="days-nav-row">
              <el-col v-for="(day, index) in days" :key="day" :span="3">
                <div class="day-cell">
                  <el-button v-if="index === 0" class="nav-arrow" @click.stop="prevWeek" :disabled="isLoading">
                    <BaseIcon name="chevron-left" icon-style="fal" />
                  </el-button>

                  <div class="day-info">
                    <span :class="['date-num', { 'is-today': isToday(day) }]">{{ formatDate(day) }}</span>
                    <span class="weekday-text">{{ formatWeekday(day) }}</span>
                  </div>

                  <el-button v-if="index === days.length - 1" class="nav-arrow" @click.stop="nextWeek" :disabled="isLoading">
                    <BaseIcon name="chevron-right" icon-style="fal" />
                  </el-button>
                </div>
              </el-col>
            </el-row>
          </el-col>
        </el-row>
      </div>

      <div class="rooms-scroll-area">
        <div v-if="!rooms.length > 0" class="no-rooms">
          該服務地點，目前尚未建立任何房型，請至左上角新增
          <i class="el-icon-top-left" style="font-weight:900" />
        </div>
        <div v-else v-for="room in rooms" :key="room.id" class="room-type-card">
          <el-row type="flex" class="room-main-content">
            <el-col :span="6" class="info-side">
              <div class="info-clickable" @click="toggleRoom(room)">
                <div class="room-img-box">
                  <img v-if="room.images && room.images.length > 0 && room.images[0] && room.images[0].path" :src="room.images[0].path" :alt="room.name" />
                  <img v-else src="@assets/images/default_room.jpg" alt="預設房型圖片" />
                </div>
                <div class="room-text-box">
                  <div class="room-name">
                    {{ room.name }} <span class="count-badge">| {{ room.rooms.length }}間</span>
                  </div>
                  <div class="pet-desc">可入住{{ room.maxPets }}隻內</div>
                  <div class="category-text">{{ allCategoriesText(room.categories) }}</div>
                </div>
                <div class="expand-icon">
                  <BaseIcon name="chevron-down" icon-style="fal" :class="{ 'is-active': activeRoom(room) }" />
                </div>
              </div>

              <div class="action-buttons">
                <router-link :to="`/hotel/modify-room/${room.id}`">
                  <el-button size="small">房型設定</el-button>
                </router-link>
                <router-link :to="`/hotel/room-scheduling/${room.id}`">
                  <el-button size="small">房況管理</el-button>
                </router-link>
              </div>
            </el-col>

            <el-col :span="18" class="data-side">
              <el-row class="full-height">
                <el-col v-for="day in room.days" :key="day.date" :span="3" class="price-grid-cell">
                  <span class="price-label">{{ dayPrice(day, day.priceStartAt) }}</span>
                </el-col>
              </el-row>
            </el-col>
          </el-row>

          <transition name="fade">
            <div v-if="activeRoom(room)" class="sub-rooms-area">
              <el-row v-for="subRoom in room.rooms" :key="subRoom.id" class="sub-room-row">
                <el-col :span="6" class="sub-room-title-col">
                  <div class="sub-title-flex">
                    <span class="sub-name">{{ subRoom.name }}</span>
                    <router-link :to="`/hotel/subroom-scheduling/${subRoom.id}`">
                      <el-link type="primary" :underline="false"> <BaseIcon name="edit" /> 房況管理 </el-link>
                    </router-link>
                  </div>
                </el-col>
                <el-col :span="18" class="sub-room-data-col">
                  <el-row>
                    <el-col v-for="subRoomDay in subRoom.days" :key="subRoomDay.date" :span="3" class="price-grid-cell sub-cell">
                      <span class="price-label">{{ dayPrice(subRoomDay, subRoomDay.priceStartAt) }}</span>
                    </el-col>
                  </el-row>
                </el-col>
              </el-row>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div v-if="activeSubTab === 'long-stay'" :class="$style.content">
      <LongStayPrice :product-id="currentBranch" />
    </div>
  </div>
</template>

<style lang="scss" module>
.container {
  background-color: #fff;
}

.content {
  position: relative;
}

.header {
  display: flex;
  height: 50px;
  border-bottom: 1px solid #eaeaea;
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #fff;
}

.branchSelector {
  flex: 1;
  background-color: #f37736; // Orange background
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
}

.dropdownLink {
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  display: block;
  width: 100%;
  text-align: center;
}

.tabs {
  flex: 2;
  display: flex;
}

.tabItem {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #999;
  cursor: pointer;
  background-color: #f9fafb;

  &.active {
    color: #f37736;
    background: white;
    font-weight: bold;
  }

  &:hover {
    color: #f37736;
    background-color: #fafafa;
  }
}
</style>

<style lang="scss" scoped>
.calendar-header-wrapper {
  border: 1px solid #e5e7eb;
  background: #fff;
  position: sticky;
  top: 50px;
  z-index: 9;

  .header-left-box {
    padding: 16px;
    border-right: 1px solid #e5e7eb;
    text-align: center;
    .room-list-title {
      margin: 0 0 8px 0;
      font-size: 14px;
      font-weight: bold;
    }
    .add-room-btn {
      width: 100%;
    }
  }
  .header-right-box {
    .month-display {
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-bottom: 1px solid #e5e7eb;
      font-weight: bold;
      cursor: pointer;
    }
  }
}
.rooms-scroll-area {
  height: calc(100vh - 215px);
  overflow-y: auto;
  .no-rooms {
    width: 100%;
    text-align: center;
    margin-top: 100px;
    font-size: 1.3rem;
  }
}

.days-nav-row {
  display: flex;
  justify-content: space-around;
  align-items: center;
  .day-cell {
    margin: 0px 7px;
    // position: relative;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    .day-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      .date-num {
        width: 32px;
        height: 32px;
        line-height: 32px;
        text-align: center;
        &.is-today {
          background: #ea6a2a;
          color: #fff;
          border-radius: 50%;
        }
      }
      .weekday-text {
        font-size: 12px;
        margin-top: 4px;
        color: #666;
      }
    }
  }
}

.nav-arrow {
  font-weight: 500;
  font-size: 20px;
  background: #f2f2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  z-index: 5;
}
::v-deep .el-button.nav-arrow {
  padding: 0px 10px;
}

.room-type-card {
  margin-top: 0;
  border: 1px solid #e5e7eb;
  border-top: none;
  background-color: #fff;

  /* 在卡片之間增加間隔，替代原本的厚邊框 */
  &:not(:first-child) {
    margin-top: 12px;
    border-top: 1px solid #e5e7eb;
  }
}

.info-side {
  background-color: #f2f2f2;
  padding: 15px;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .info-clickable {
    display: flex;
    cursor: pointer;
    .room-img-box img {
      width: 91px;
      height: 63px;
      border-radius: 8px;
      object-fit: cover;
    }
    .room-text-box {
      flex: 1;
      margin-left: 12px;
      text-align: left;
      .room-name {
        font-weight: bold;
        font-size: 15px;
      }
      .count-badge {
        font-size: 12px;
        color: #6c6b6b;
      }
      .pet-desc,
      .category-text {
        font-size: 12px;
        color: #666;
        margin-top: 2px;
        white-space: pre-line;
      }
    }
    .expand-icon svg {
      transition: transform 0.3s;
      &.is-active {
        transform: rotate(180deg);
      }
    }
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;
  .el-button {
    flex: 1;
    border-radius: 8px;
  }
}

.price-grid-cell {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #e5e7eb;
  background: #f2f2f2;
  margin: 0px 7px;
  .price-label {
    font-size: 13px;
  }
  &.sub-cell {
    height: 65px;
    background: #fff;
  }
}

/* 子房間展開區 */
.sub-room-row {
  border-top: 1px solid #e5e7eb;
  background-color: #fff; // 確保子房間背景為白色
  display: flex;

  .sub-room-title-col {
    background: #fff;
    border-right: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    padding: 10px 15px; // 稍微增加上下邊距，讓空間感更好

    .sub-title-flex {
      width: 100%;
      display: flex;
      justify-content: space-around; // 這是上一版使用的對齊方式
      align-items: center;

      .sub-name {
        font-weight: 400; // 上一版採用較輕的字重
        color: #000000;
        font-size: 14px;
      }

      .edit-btn {
        color: #ea6a2a; // 橘色文字
        font-weight: 400;
        font-size: 14px;

        i,
        svg {
          margin-right: 4px;
        }

        &:hover {
          opacity: 0.8;
        }
      }
    }
  }

  .sub-room-data-col {
    .price-grid-cell.sub-cell {
      height: 65px; // 保持上一版定義的高度
      background-color: #ffffff;
      border-right: 1px solid #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0px 7px;

      .price-text {
        color: #6c6b6b;
        font-size: 13px;
      }
    }
  }
}

.full-height {
  height: 100%;
}
</style>

<style lang="scss">
.custom-branch-dropdown {
  margin-top: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  min-width: 200px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  .popper__arrow {
    display: none !important;
  }

  .el-dropdown-menu__item {
    text-align: center;
    font-size: 16px;
    padding: 12px 0;
    line-height: normal;

    &.is-active {
      background-color: #f37736;
      color: #fff;

      &:hover {
        background-color: #f37736;
        color: #fff;
      }
    }

    &:not(.is-active):hover {
      background-color: #fafafa;
      color: #606266;
    }
  }
}
</style>
